import React, { useMemo } from 'react';
import KpiTiles from './KpiTiles';
import WaffleChart from './WaffleChart';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';

// ============================================================================
// Type Definitions
// ============================================================================

export interface AxisFormat {
  format_type?: 'number' | 'currency' | 'percent' | 'date' | 'quarter' | 'custom';
  pattern?: string;
  prefix?: string;
  suffix?: string;
  decimal_places?: number;
}

export interface AxisConfig {
  label?: string;
  format?: AxisFormat;
  scale?: 'linear' | 'log' | 'percent';
  min?: number;
  max?: number;
  label_rotation?: number;
}

export interface DataLabelConfig {
  show: boolean;
  position?: 'top' | 'center' | 'bottom' | 'inside' | 'outside';
  format?: AxisFormat;
  font_size?: number;  // Font size in pixels (default: 12)
}

export interface LegendConfig {
  show: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
  orientation?: 'horizontal' | 'vertical';
}

export interface ColorConfig {
  scheme?: 'default' | 'blue' | 'green' | 'warm' | 'cool' | 'monochrome';
  colors?: string[];
  primary?: string;
}

export interface SortConfig {
  sort_by?: 'value' | 'label' | 'none';
  direction?: 'asc' | 'desc';
  limit?: number;
}

export interface BarChartOptions {
  orientation?: 'vertical' | 'horizontal';
  stacking?: 'none' | 'stacked' | 'percent';
  bar_width?: number;
  border_radius?: number;
}

export interface LineChartOptions {
  show_points?: boolean;
  line_style?: 'solid' | 'dashed' | 'dotted';
  area_fill?: boolean;
  curve_type?: 'linear' | 'smooth' | 'step';
  stroke_width?: number;
}

export interface PieChartOptions {
  show_percentages?: boolean;
  inner_radius?: number;
  outer_radius?: number;
  start_angle?: number;
}

export interface FilterConfig {
  column: string;
  label: string;
}

export interface ReconMetadata {
  left_table: string;
  right_table: string;
  join_key: string;
  left_label?: string;
  right_label?: string;
}

export interface ChartConfig {
  chart_type: 'bar' | 'line' | 'pie' | 'tiles' | 'waffle';
  x_column: string;
  y_column: string;
  title?: string;
  subtitle?: string;
  x_axis?: AxisConfig;
  y_axis?: AxisConfig;
  x_label?: string;  // Legacy
  y_label?: string;  // Legacy
  data_labels?: DataLabelConfig;
  legend?: LegendConfig;
  colors?: ColorConfig;
  sorting?: SortConfig;
  bar_options?: BarChartOptions;
  line_options?: LineChartOptions;
  pie_options?: PieChartOptions;
  column_order?: string[];
  tile_formats?: Record<string, string>;
  filters?: FilterConfig[];
  source_table?: string;
  source_sql?: string;
  recon_metadata?: ReconMetadata;
}

// Formatting config from formatter sub-agent (enum-based)
export interface FormattingConfig {
  x_format: string;
  y_format: string;
  color_scheme: string;
  show_legend: boolean;
  show_data_labels: boolean;
}

interface ChartRendererProps {
  config: ChartConfig;
  data: Record<string, unknown>[];
  formatting?: FormattingConfig;
}

// ============================================================================
// Color Schemes
// ============================================================================

const COLOR_SCHEMES: Record<string, string[]> = {
  default: ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'],
  blue: ['#003f5c', '#2f4b7c', '#665191', '#a05195', '#d45087', '#f95d6a', '#ff7c43', '#ffa600'],
  green: ['#006d2c', '#31a354', '#74c476', '#a1d99b', '#c7e9c0', '#e5f5e0', '#f7fcf5', '#00441b'],
  warm: ['#b30000', '#e34a33', '#fc8d59', '#fdbb84', '#fdd49e', '#fee8c8', '#fff7ec', '#7f0000'],
  cool: ['#0c2c84', '#225ea8', '#1d91c0', '#41b6c4', '#7fcdbb', '#c7e9b4', '#edf8b1', '#ffffd9'],
  monochrome: ['#e5e7eb', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#111827'],
};

// ============================================================================
// Format Code Mapping (deterministic enum -> AxisFormat)
// ============================================================================

const FORMAT_CODE_MAP: Record<string, AxisFormat> = {
  'none': {},
  'quarter_q1_yyyy': { format_type: 'quarter', pattern: 'Q{q} {yyyy}' },
  'quarter_yyyy_q1': { format_type: 'quarter', pattern: '{yyyy}-Q{q}' },
  'date_mmm_yyyy': { format_type: 'date', pattern: 'MMM YYYY' },
  'date_yyyy_mm': { format_type: 'date', pattern: 'YYYY-MM' },
  'currency_usd': { format_type: 'currency', prefix: '$' },
  'currency_usd_k': { format_type: 'currency', prefix: '$', suffix: 'K', decimal_places: 1 },
  'currency_usd_m': { format_type: 'currency', prefix: '$', suffix: 'M', decimal_places: 1 },
  'number_comma': { format_type: 'number' },
  'number_k': { format_type: 'number', suffix: 'K', decimal_places: 1 },
  'number_m': { format_type: 'number', suffix: 'M', decimal_places: 1 },
  'number_decimal_2': { format_type: 'number', decimal_places: 2 },
  'percent': { format_type: 'percent' },
};

function getFormatFromCode(code: string): AxisFormat | undefined {
  return FORMAT_CODE_MAP[code] || undefined;
}

// ============================================================================
// Formatting Utilities
// ============================================================================

function formatValue(value: unknown, format?: AxisFormat): string {
  if (value === null || value === undefined) return '-';

  let numValue = typeof value === 'number' ? value : parseFloat(String(value));

  if (format) {
    let result = '';

    if (!isNaN(numValue)) {
      const decimals = format.decimal_places ?? 0;

      // Scale the number if suffix indicates K or M
      let scaledValue = numValue;
      if (format.suffix === 'K') {
        scaledValue = numValue / 1000;
      } else if (format.suffix === 'M') {
        scaledValue = numValue / 1000000;
      }

      switch (format.format_type) {
        case 'currency':
        case 'number':
          result = scaledValue.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
          break;
        case 'percent':
          // Value should already be the display percentage (e.g., 45 for 45%)
          // SQL is responsible for multiplying by 100 if needed
          result = numValue.toFixed(decimals) + '%';
          break;
        case 'quarter':
          // Assuming value is a date string or quarter number
          if (format.pattern) {
            result = formatQuarter(value, format.pattern);
          } else {
            result = String(value);
          }
          break;
        case 'date':
          if (format.pattern) {
            result = formatDate(value, format.pattern);
          } else {
            result = String(value);
          }
          break;
        default:
          result = scaledValue.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
      }
    } else {
      result = String(value);
    }

    // Apply prefix and suffix
    if (format.prefix) result = format.prefix + result;
    if (format.suffix) result = result + format.suffix;

    return result;
  }

  return String(value);
}

function formatQuarter(value: unknown, pattern: string): string {
  const dateStr = String(value);

  // Try to extract quarter info from common formats like "2024-Q1", "Q1-2024", "2024Q1", "Q1 2024"
  const quarterPatterns = [
    /(\d{4})[-\s]?Q(\d)/i,  // "2024-Q1", "2024 Q1", "2024Q1"
    /Q(\d)[-\s]?(\d{4})/i,  // "Q1-2024", "Q1 2024", "Q12024"
  ];

  for (const regex of quarterPatterns) {
    const match = dateStr.match(regex);
    if (match) {
      // Determine which group is year and which is quarter based on pattern
      let year: string, quarter: string;
      if (regex.source.startsWith('(\\d{4})')) {
        year = match[1];
        quarter = match[2];
      } else {
        quarter = match[1];
        year = match[2];
      }

      return pattern
        .replace('{q}', quarter)
        .replace('{Q}', quarter)
        .replace('{yyyy}', year)
        .replace('{YYYY}', year)
        .replace('{yy}', year.slice(-2));
    }
  }

  // Try to parse as date
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const year = date.getFullYear();

    return pattern
      .replace('{q}', String(quarter))
      .replace('{Q}', String(quarter))
      .replace('{yyyy}', String(year))
      .replace('{YYYY}', String(year))
      .replace('{yy}', String(year).slice(-2));
  }

  return dateStr;
}

function formatDate(value: unknown, pattern: string): string {
  const dateStr = String(value);
  const date = new Date(dateStr);

  if (!isNaN(date.getTime())) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fullMonths = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    return pattern
      .replace('YYYY', String(date.getFullYear()))
      .replace('YY', String(date.getFullYear()).slice(-2))
      .replace('MMMM', fullMonths[date.getMonth()])
      .replace('MMM', months[date.getMonth()])
      .replace('MM', String(date.getMonth() + 1).padStart(2, '0'))
      .replace('DD', String(date.getDate()).padStart(2, '0'));
  }

  return String(value);
}

// ============================================================================
// Data Processing
// ============================================================================

function processData(
  data: Record<string, unknown>[],
  config: ChartConfig
): Record<string, unknown>[] {
  let processed = [...data];

  // Apply sorting
  console.log('[processData] config.sorting:', config.sorting);
  if (config.sorting) {
    const { sort_by, direction, limit } = config.sorting;
    console.log('[processData] Applying sort:', sort_by, direction);

    if (sort_by === 'value') {
      processed.sort((a, b) => {
        const aVal = Number(a[config.y_column]) || 0;
        const bVal = Number(b[config.y_column]) || 0;
        return direction === 'desc' ? bVal - aVal : aVal - bVal;
      });
    } else if (sort_by === 'label') {
      processed.sort((a, b) => {
        const aVal = String(a[config.x_column]);
        const bVal = String(b[config.x_column]);
        return direction === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      });
    }

    if (limit && limit > 0) {
      processed = processed.slice(0, limit);
    }
  }

  return processed;
}

// ============================================================================
// Chart Component
// ============================================================================

const ChartRenderer: React.FC<ChartRendererProps> = ({ config, data, formatting }) => {
  // Debug: log sorting config
  console.log('[ChartRenderer] Received config.sorting:', config.sorting);

  // Process data with sorting/limiting
  const processedData = useMemo(() => processData(data, config), [data, config]);

  if (!processedData || processedData.length === 0) {
    return <div className="chart-empty">No data available for chart</div>;
  }

  const { chart_type, x_column, y_column, title, subtitle } = config;

  // Validate columns exist in data
  const firstRow = processedData[0];
  if (!(x_column in firstRow) || !(y_column in firstRow)) {
    return (
      <div className="chart-error">
        Chart configuration error: columns "{x_column}" or "{y_column}" not found in data
      </div>
    );
  }

  // Get colors - formatting overrides config
  const getColors = (): string[] => {
    // First check formatting from formatter sub-agent
    if (formatting?.color_scheme && COLOR_SCHEMES[formatting.color_scheme]) {
      return COLOR_SCHEMES[formatting.color_scheme];
    }
    // Fall back to config
    if (config.colors?.colors) return config.colors.colors;
    if (config.colors?.scheme) return COLOR_SCHEMES[config.colors.scheme] || COLOR_SCHEMES.default;
    return COLOR_SCHEMES.default;
  };

  const colors = getColors();
  const primaryColor = config.colors?.primary || colors[0];

  // Get axis labels (prefer new x_axis/y_axis, fall back to legacy x_label/y_label)
  const xLabel = config.x_axis?.label || config.x_label;
  const yLabel = config.y_axis?.label || config.y_label;
  const xRotation = config.x_axis?.label_rotation ?? -45;

  // Get axis formats - formatting from formatter sub-agent overrides config
  const xAxisFormat = formatting?.x_format
    ? getFormatFromCode(formatting.x_format)
    : config.x_axis?.format;
  const yAxisFormat = formatting?.y_format
    ? getFormatFromCode(formatting.y_format)
    : config.y_axis?.format;

  // Format tick values
  const xTickFormatter = (value: unknown) => formatValue(value, xAxisFormat);
  const yTickFormatter = (value: unknown) => formatValue(value, yAxisFormat);

  // Legend settings - formatting overrides config
  const showLegend = formatting?.show_legend ?? config.legend?.show !== false;
  const legendPosition = config.legend?.position || 'bottom';
  const legendLayout = config.legend?.orientation === 'vertical' ? 'vertical' : 'horizontal';

  // Data label settings - formatting overrides config
  const showDataLabels = formatting?.show_data_labels ?? config.data_labels?.show ?? false;
  const dataLabelFormatter = (value: unknown) => formatValue(value, yAxisFormat);
  const dataLabelFontSize = config.data_labels?.font_size ?? 12;

  // ============================================================================
  // Bar Chart
  // ============================================================================

  const renderBarChart = () => {
    const opts = config.bar_options || {};
    const isHorizontal = opts.orientation === 'horizontal';
    const radius = opts.border_radius || 4;

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={processedData}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          {isHorizontal ? (
            <>
              <YAxis
                dataKey={x_column}
                type="category"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={xTickFormatter}
              />
              <XAxis
                type="number"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={yTickFormatter}
                label={yLabel ? { value: yLabel, position: 'bottom', fill: '#9CA3AF' } : undefined}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={x_column}
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                angle={xRotation}
                textAnchor="end"
                height={80}
                tickFormatter={xTickFormatter}
                label={xLabel ? { value: xLabel, position: 'bottom', fill: '#9CA3AF', dy: 30 } : undefined}
              />
              <YAxis
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={yTickFormatter}
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: '#9CA3AF' } : undefined}
                domain={[config.y_axis?.min ?? 'auto', config.y_axis?.max ?? 'auto']}
                scale={config.y_axis?.scale === 'log' ? 'log' : 'auto'}
              />
            </>
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F3F4F6',
            }}
            formatter={(value: unknown) => [formatValue(value, yAxisFormat), y_column]}
          />
          {showLegend && (
            <Legend
              wrapperStyle={{ color: '#9CA3AF' }}
              verticalAlign={legendPosition === 'top' ? 'top' : 'bottom'}
              layout={legendLayout as 'horizontal' | 'vertical'}
            />
          )}
          <Bar
            dataKey={y_column}
            fill={primaryColor}
            radius={isHorizontal ? [0, radius, radius, 0] : [radius, radius, 0, 0]}
          >
            {showDataLabels && (
              <LabelList
                dataKey={y_column}
                position={config.data_labels?.position || 'top'}
                formatter={dataLabelFormatter}
                fill="#9CA3AF"
                fontSize={dataLabelFontSize}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // ============================================================================
  // Line Chart
  // ============================================================================

  const renderLineChart = () => {
    const opts = config.line_options || {};
    const showPoints = opts.show_points !== false;
    const strokeWidth = opts.stroke_width || 2;
    const curveType = opts.curve_type === 'step' ? 'stepAfter' :
                      opts.curve_type === 'smooth' ? 'monotone' : 'linear';

    // Use AreaChart if area_fill is true
    if (opts.area_fill) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey={x_column}
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
              angle={xRotation}
              textAnchor="end"
              height={80}
              tickFormatter={xTickFormatter}
              label={xLabel ? { value: xLabel, position: 'bottom', fill: '#9CA3AF', dy: 30 } : undefined}
            />
            <YAxis
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
              tickFormatter={yTickFormatter}
              label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: '#9CA3AF' } : undefined}
              domain={[config.y_axis?.min ?? 'auto', config.y_axis?.max ?? 'auto']}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#F3F4F6',
              }}
              formatter={(value: unknown) => [formatValue(value, yAxisFormat), y_column]}
            />
            {showLegend && <Legend wrapperStyle={{ color: '#9CA3AF' }} />}
            <Area
              type={curveType}
              dataKey={y_column}
              stroke={primaryColor}
              fill={primaryColor}
              fillOpacity={0.3}
              strokeWidth={strokeWidth}
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey={x_column}
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
            angle={xRotation}
            textAnchor="end"
            height={80}
            tickFormatter={xTickFormatter}
            label={xLabel ? { value: xLabel, position: 'bottom', fill: '#9CA3AF', dy: 30 } : undefined}
          />
          <YAxis
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
            tickFormatter={yTickFormatter}
            label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: '#9CA3AF' } : undefined}
            domain={[config.y_axis?.min ?? 'auto', config.y_axis?.max ?? 'auto']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F3F4F6',
            }}
            formatter={(value: unknown) => [formatValue(value, yAxisFormat), y_column]}
          />
          {showLegend && <Legend wrapperStyle={{ color: '#9CA3AF' }} />}
          <Line
            type={curveType}
            dataKey={y_column}
            stroke={primaryColor}
            strokeWidth={strokeWidth}
            strokeDasharray={opts.line_style === 'dashed' ? '5 5' : opts.line_style === 'dotted' ? '2 2' : undefined}
            dot={showPoints ? { fill: primaryColor, strokeWidth: 2 } : false}
            activeDot={{ r: 8 }}
          >
            {showDataLabels && (
              <LabelList
                dataKey={y_column}
                position={config.data_labels?.position || 'top'}
                formatter={dataLabelFormatter}
                fill="#9CA3AF"
                fontSize={dataLabelFontSize}
              />
            )}
          </Line>
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // ============================================================================
  // Pie Chart
  // ============================================================================

  const renderPieChart = () => {
    const opts = config.pie_options || {};
    const showPercentages = opts.show_percentages !== false;
    const innerRadius = opts.inner_radius || 0;
    const outerRadius = opts.outer_radius || 120;
    const startAngle = opts.start_angle || 0;

    return (
      <ResponsiveContainer width="100%" height={450}>
        <PieChart margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <Pie
            data={processedData}
            dataKey={y_column}
            nameKey={x_column}
            cx="50%"
            cy="45%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={90 - startAngle}
            endAngle={450 - startAngle}
            label={showPercentages
              ? ({ percent }) => `${((percent ?? 0) * 100).toFixed(1)}%`
              : ({ name }) => String(name)
            }
            labelLine={{ stroke: '#9CA3AF' }}
          >
            {processedData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F3F4F6',
            }}
            formatter={(value: unknown) => [formatValue(value, yAxisFormat), y_column]}
          />
          {showLegend && (
            <Legend
              layout={legendLayout as 'horizontal' | 'vertical'}
              verticalAlign={legendPosition === 'top' ? 'top' : 'bottom'}
              align="center"
              wrapperStyle={{ color: '#9CA3AF', paddingTop: '20px' }}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    );
  };

  if (chart_type === 'tiles') {
    // Resolve per-tile formats from config
    const tileFormats = config.tile_formats
      ? Object.fromEntries(
          Object.entries(config.tile_formats)
            .map(([label, code]) => [label, getFormatFromCode(code)])
            .filter(([, fmt]) => fmt !== undefined)
        ) as Record<string, AxisFormat>
      : undefined;

    return (
      <KpiTiles
        data={processedData}
        xColumn={x_column}
        yColumn={y_column}
        title={title}
        columnOrder={config.column_order}
        valueFormat={yAxisFormat}
        tileFormats={tileFormats}
      />
    );
  }

  if (chart_type === 'waffle') {
    return (
      <WaffleChart
        data={processedData}
        xColumn={x_column}
        yColumn={y_column}
        title={title}
        subtitle={subtitle}
        colors={getColors()}
      />
    );
  }

  return (
    <div className="chart-container">
      {title && <h3 className="chart-title">{title}</h3>}
      {subtitle && <p className="chart-subtitle">{subtitle}</p>}
      {chart_type === 'bar' && renderBarChart()}
      {chart_type === 'line' && renderLineChart()}
      {chart_type === 'pie' && renderPieChart()}
    </div>
  );
};

export default ChartRenderer;
